import classNames from "classnames";
import React from "react";

import type { IBaseComponent } from "../models/formFields";
import { FormAdaText } from "./FormAdaText";
import { FormHelpText } from "./FormHelpText";
import { FormLabel } from "./FormLabel";
import { FormTooltip } from "./FormTooltip";
import { FormValidationError } from "./FormValidationError";
import { FormWarning } from "./FormWarning";
import { showValidationErrors } from "./hooks/formUtils";

/**
 * Full wrapper for form input fields
 */
export const FormInputFieldWrapper = (
    props: IBaseComponent & {
        name: string;
        type?: string;
        value?: string | number | boolean | null;
        label?: string;
        placeholder?: string;
        labelCSSClass?: string;
        labelPlacement?:
            | "top"
            | "left"
            | "right"
            | "bottom"
            | "outside"
            | "dynamic"
            | "static";
        onLabelClick?: () => void;
        adaText?: string;
        wrapperCSSClass?: string;
        focused?: boolean;
        isRequired: boolean;
        children: React.ReactNode;
    },
): React.ReactNode => {
    const classes = props.wrapperCSSClass
        ? props.wrapperCSSClass
        : classNames({
              ["form__field"]: true,
              ["form__field--required"]: props.isRequired,
              ["form__field--optional"]: !props.isRequired,
              ["form__field--has-errors"]: showValidationErrors(props),
              [`form__field--${props.type}`]: !!props.type,
              [`form__field--${props.name}`]: !!props.name,
              ["form__field--focused"]: props.focused,
              ["form__field--not-blank"]: !!props.value,
          });

    return (
        <div className={classes}>
            {(props.label || props.placeholder) && (
                <FormLabel
                    id={props.id}
                    label={props.label}
                    placeholder={props.placeholder}
                    labelCSSClass={props.labelCSSClass}
                    labelPlacement={props.labelPlacement}
                    onLabelClick={props.onLabelClick}
                />
            )}
            {props.tooltipText && (
                <FormTooltip
                    tooltipText={props.tooltipText}
                    tooltipLink={props.tooltipLink}
                    tooltipClass={props.tooltipClass}
                />
            )}
            {props.children}
            {props.errors && <FormValidationError {...props} id={props.id} />}
            {props.helpText && (
                <FormHelpText
                    id={props.id}
                    helpText={props.helpText}
                    helpTextClass={props.helpTextClass}
                />
            )}
            {props.warningText && (
                <FormWarning
                    warningTitle={props.warningTitle}
                    warningText={props.warningText}
                />
            )}
            {props.adaText && (
                <FormAdaText id={props.id} adaText={props.adaText} />
            )}
        </div>
    );
};
